import { Placeholder } from "./interfaces/placeholder";
import {
  MarketingClientConstants
} from "./constants/marketing-client-constants";
import { CollectionIdsObject, CtaObject, RegistrationWallObject, Utils } from "./utils/utils";
import { httpInstance } from "./http/http";
import { UserData } from "./user-data/user-data";
import { BigDataHelper } from "./helpers/big-data-helper";
import { templatesDTO } from "./interfaces/templates-DTO";
import { RequestPaths, SquidURLs, URLBase } from "./constants/url-constants";
import { MCTemplate, MCTemplatesResponse } from "./custom-types/custom-types";
import { BigData } from "./marketing-client-bigdata";
import { HttpResponse } from "./http/sub-types/http-response";
import { Capping, CappingData } from "./marketing-client-capping";
import { MarketingClientCustomDataLayer } from "./marketing-client-custom-datalayer";
import { Utilization } from "./marketing-client-utilization";
import { MarketingClientBanners } from "./marketing-client-banners";
import { DOMHelper } from "./helpers/dom-helper";
import { MarketingClientAttributesConstants } from "./constants/marketing-client-attributes-constants";
import { BigDataObjectConstants } from "./constants/big-data-object-constants";
import { PredefinedIcons } from "./constants/predefined-icons-constants";
import { DeviceInformationHelper, DeviceType } from "./helpers/device-information-helper";
import { marketingClientDataLayer } from "./marketing-client-datalayer";
import { BigDataEventConstants } from "./constants/big-data-constants";
import { EventType } from "./constants/event-type-constants";

export const WYBORCZA_PL = window["wyborcza_pl"] || {};
export const GAZETA_PL = window["gazeta_pl"] || {};

export interface DmElement {
  element: HTMLElement;
  isPlanB: boolean;
  dataId: CollectionIdsObject;
  dataCta: CtaObject;
  dataRW?: RegistrationWallObject;
  clickID?: string;
  visible: boolean;
}

export class MarketingClient {
  bigDataObject = new BigData();
  dataLayerObject = new MarketingClientCustomDataLayer();
  utilizationObject = new Utilization(httpInstance);
  bannersObject: MarketingClientBanners;
  capping = new Capping(httpInstance);
  userData: UserData = new UserData();

  started = false;
  placeholders: Array<Placeholder> = [];
  bigDataGUID: string | null;
  isMobile: boolean = false;
  dmElements: Array<DmElement> = [];

  init() {
    setTimeout(() => {
      let bigDataGUID = BigDataHelper.getBigDataGUID();
      this.isMobile = DeviceInformationHelper.getDeviceType() === DeviceType.MOBILE;

      if (!!bigDataGUID) {
        this.start();
      } else {
        window.addEventListener("message", (event: MessageEvent) => {
          this.onMessageReceived(event);
        }, false);
      }
    }, 200);
  }

  start() {
    this.userData.getProfile();
    this.bigDataGUID = BigDataHelper.getBigDataGUID() as string;
    this.pushPlaceholders();

    if (Utils.isNullOrUndefined(this.bigDataGUID) || !Utils.stringNotEmpty(this.bigDataGUID)) {
      this.bigDataGUID = "NOGUID";
      this.userData.profile.anonimowy = true;
    }

    this.utilizationObject.setGUID(this.bigDataGUID);
    this.capping.setGUID(this.bigDataGUID);

    if (this.placeholders.length > 0 && !this.userData.profile.disable) {
      this.getActionsFromDomain().then(() => {
        console.info("Successfully downloaded actions from domain");
      }, () => {
        console.warn("Failed to load actions from domain");
      });
    } else {
      this.initDefaultAdds();
    }

    marketingClientDataLayer.init();
  }

  pushPlaceholders(): void {
    const rootElement = document.querySelector(MarketingClientConstants.rootElement);

    if (rootElement) {
      DOMHelper.forEachQuerySelector(
        `*[${MarketingClientAttributesConstants.placeholderId}]`,
        (element: Element) => {
          let templateId = element.getAttribute(MarketingClientAttributesConstants.placeholderId),
            isSwimmer = false,
            isDDBox = false;

          const isSwimmerAttribute = element.getAttribute(MarketingClientAttributesConstants.isSwimmer),
            isDDBoxAttribute = element.getAttribute(MarketingClientAttributesConstants.isDDBox);

          if (Utils.stringNotEmpty(isSwimmerAttribute)) {
            isSwimmer = isSwimmerAttribute === "true";
          }

          if (Utils.stringNotEmpty(isDDBoxAttribute)) {
            isDDBox = isDDBoxAttribute === "true";
          }

          if (Utils.stringNotEmpty(templateId)) {
            templateId = templateId as string;
            this.placeholders.push({ element, templateId, isSwimmer, isDDBox } as Placeholder);
          }
        },
        rootElement
      );
    }
  }

  refreshPlaceholdersReferences() {
    this.placeholders = this.placeholders.map(placeholder => {
      const id = placeholder.element.getAttribute(MarketingClientAttributesConstants.placeholderId),
        element = document.querySelector(`[${MarketingClientAttributesConstants.placeholderId}="${id}"]`);

      placeholder.element = element ? element : placeholder.element;
      return placeholder;
    });
  }

  prepareDTOForMarketingProxy(isHarmonyTest: boolean): templatesDTO {
    const urlParams = Utils.getURLParamsAsObject(),
      pageViewId = BigDataHelper.getBigDataOrCookieValue(
        BigDataObjectConstants.pageViewCookie,
        BigDataObjectConstants.pageViewBigData
      ),
      visitId = BigDataHelper.getBigDataOrCookieValue(
        BigDataObjectConstants.visitIdCookie,
        BigDataObjectConstants.visitIdBigData
      );

    let profile = this.userData.profile;

    if (isHarmonyTest) {
      let profileFromUrl = urlParams[MarketingClientConstants.overwriteProfileUrlParam];

      if (!Utils.isNullOrUndefined(profileFromUrl)) {
        profileFromUrl = profileFromUrl as string;

        if (Utils.stringNotEmpty(profileFromUrl)) {
          try {
            profile = JSON.parse(atob(decodeURIComponent(profileFromUrl)));
          } catch (e) {
            console.warn("URL profile is empty or cannot be converted -> cannot overwrite base profile");
          }
        }
      }

      // @ts-ignore
      if (!window[MarketingClientConstants.encodeHarmonyProfileHelperKey]) {
        // @ts-ignore
        window[MarketingClientConstants.encodeHarmonyProfileHelperKey] = (profile: object): string => {
          return encodeURIComponent(btoa(JSON.stringify(profile)));
        };
      }

      // @ts-ignore
      if (!window[MarketingClientConstants.decodeHarmonyProfileHelperKey]) {
        // @ts-ignore
        window[MarketingClientConstants.decodeHarmonyProfileHelperKey] = (profile: string): object => {
          return JSON.parse(atob(decodeURIComponent(profile)));
        };
      }
    }

    const template: templatesDTO = {
      guid: this.bigDataGUID,
      visitId: visitId,
      params: {
        [BigDataObjectConstants.artPvidParam]: pageViewId
      },
      profiles: profile,
      templates: [...this.getTemplateIdForPlaceholders()]
    };

    return template;
  }

  getTemplateIdForPlaceholders(): string[] {
    return this.placeholders.map((placeholder) => placeholder.templateId);
  }

  getActionsFromDomain(): Promise<any> {
    const isHarmonyTest = Utils.getURLParamsAsObject()[MarketingClientConstants.allowHarmonyTestingUrlParam] as boolean,
      payload = this.prepareDTOForMarketingProxy(isHarmonyTest);

    let timeoutFired = false,
      timeoutID: number;

    timeoutID = window.setTimeout(() => {
      timeoutFired = true;
      this.initDefaultAdds();
    }, 3000);

    const resultantPromise = httpInstance
      .post({
        url: `${isHarmonyTest ? URLBase.TEST : URLBase.PROD}${RequestPaths.TEMPLATE}`,
        data: payload
      });

    resultantPromise.then(
      (response: HttpResponse) => {
        clearTimeout(timeoutID);

        if (!Utils.isNullOrUndefined(response.data)) {
          const parsedResponse = response.data as MCTemplatesResponse;
          this.initAdds(parsedResponse);
        }
      },
      (error: HttpResponse) => {
        if (!timeoutFired) {
          this.initDefaultAdds();
        }
      }
    );

    return resultantPromise;
  }

  initAdds(adsObject: MCTemplatesResponse): void {
    this.placeAdds(adsObject.templates);

    this.utilizationObject.init();
    // add referers ?? -> Wawa czy my? Utils.addReferrer();

    this.bannersObject = new MarketingClientBanners(this.capping, this.utilizationObject, this.isMobile);
    this.bannersObject.init();

    // tracker parameters, bind login ?

    // init activation modules ?

    // init newsletters ?
  }

  initDefaultAdds() {
    this.refreshPlaceholdersReferences();
    this.placeholders.forEach((placeholder) => {
      this.initDefaultAdd(placeholder.element);
    });
  }

  initDefaultAdd(placeholder: Element) {
    placeholder.classList.add(MarketingClientAttributesConstants.initializedClass);
  }

  placeAdds(ads: Array<MCTemplate>): void {
    const isVideoAd = document.querySelector(MarketingClientAttributesConstants.videoClassSelector) !== null;

    if (this.placeholders.length === ads.length) {
      this.refreshPlaceholdersReferences();
      this.placeholders.forEach((placeholder: Placeholder, index: number) => {
        this.placeAdd(ads[index], placeholder, isVideoAd);
      });
    }

    this.sendDisplayEventOnScroll();
    this.dataLayerObject.init();
  }

  placeAdd(ad: MCTemplate, placeholder: Placeholder, isVideoAd: boolean) {
    const { campaignId = "", actionId = "", touchPointId = "", html = "" } = ad,
      isSwimmerOrDDbox = placeholder.isSwimmer || placeholder.isDDBox,
      isNotSwimmerOrDDboxAndVideoAd = !(isSwimmerOrDDbox && isVideoAd),
      dataId = {
        campaignId: campaignId,
        actionId: actionId,
        touchPointId: touchPointId
      } as CollectionIdsObject,
      dataToSend: CappingData = dataId as CappingData;

    let dmElement: DmElement | null = null;

    if (html && html !== "" && isNotSwimmerOrDDboxAndVideoAd) {
      this.addContent(placeholder, html, campaignId || "", actionId || "", touchPointId || "");
      this.addPredefinedIcon(placeholder.element);

      if (!isSwimmerOrDDbox) {
        this.capping.sendCapping(dataToSend);
        dmElement = this.prepareDmElement(placeholder, false, dataId);
      }
    } else {
      this.initDefaultAdd(placeholder.element);

      dmElement = this.createDmElementForPlanB(placeholder);
    }

    if (dmElement) {
      this.sendBigDataEvent(BigDataEventConstants.onLoad, dmElement);
      this.sendDisplayEventIfElementIsVisible(dmElement, dataToSend);
    }
  }

  createDmElementForPlanB(placeholder: Placeholder): DmElement | null {
    return placeholder.element.children.length ? this.prepareDmElement(placeholder, true, {}) : null;
  }

  sendDisplayEventIfElementIsVisible(dmElement: DmElement, dataToSend: CappingData) {
    const dmElementIsVisibleByUser = DOMHelper.elementIsVisible(dmElement.element) && dmElement.element.getBoundingClientRect().top <= window.innerHeight;

    if (dmElementIsVisibleByUser) {
      this.sendBigDataEvent(BigDataEventConstants.onDisplay, dmElement);

      if (!dmElement.isPlanB) {
        this.capping.sendCapping(dataToSend, true);
      }
    } else {
      this.dmElements.push(dmElement);
    }
  }

  sendDisplayEventOnScroll() {
    if (this.dmElements.length) {
      window.addEventListener(EventType.SCROLL, () => {
        this.dmElements.forEach((dmElement) => {
          const scroll = window.scrollY || 0;

          if (!dmElement.visible && scroll + window.innerHeight * 0.7 >= dmElement.element.getBoundingClientRect().top + scroll) {
            dmElement.visible = true;

            this.sendBigDataEvent(BigDataEventConstants.onDisplay, dmElement);

            if (!dmElement.isPlanB) {
              this.capping.sendCapping(dmElement.dataId as CappingData, true);
            }
          }
        });
      });
    }
  }

  addContent(placeholder: Placeholder, targetHTML: string, parsedCampaignId: string, parsedActionId: string, parsedTouchPointId: string) {
    placeholder.element.innerHTML = targetHTML;
    placeholder.element.setAttribute(MarketingClientAttributesConstants.campaignId, parsedCampaignId);
    placeholder.element.setAttribute(MarketingClientAttributesConstants.actionId, parsedActionId);
    placeholder.element.setAttribute(MarketingClientAttributesConstants.touchPointId, parsedTouchPointId);
    placeholder.element.classList.add(MarketingClientAttributesConstants.initializedClass);
  }

  sendBigDataEvent(eventName: string, dmElement: DmElement) {
    const attribute = BigData.prepareDataOceanAttribute(dmElement.dataCta, dmElement.dataId, dmElement.dataRW);

    BigData.sendDataOceanEvent(eventName, attribute);
  }

  prepareDmElement(placeholder: Placeholder, isPlanB: boolean, dataId: CollectionIdsObject): DmElement {
    const planB = "planB",
      element = placeholder.element as HTMLElement,
      dataCta = Utils.prepareCtaObject(element),
      dataRW = Utils.prepareRegistrationWallObject(element);

    if (isPlanB) {
      dataId = {
        campaignId: planB,
        actionId: planB,
        touchPointId: planB
      }
    }

    return {
      element,
      visible: false,
      dataId,
      dataCta,
      dataRW,
      isPlanB
    };
  }

  addPredefinedIcon(placeholder: Element) {
    const useElements = placeholder.querySelectorAll("use");

    let attribute: string | null = "",
      predefinedIconElement = Array.from(useElements).find((element) => {
        attribute = element.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

        return attribute && Object.keys(PredefinedIcons).includes(attribute);
      })

    const parent: HTMLElement | null | undefined = predefinedIconElement?.parentElement;

    if (attribute && predefinedIconElement && parent) {
      parent.setAttribute("viewBox", PredefinedIcons[attribute].viewBox);
      parent.innerHTML = PredefinedIcons[attribute].content;
    }
  }

  onMessageReceived(event: MessageEvent) {
    const baseDomain = document.location.protocol === "https:" ? SquidURLs.https : SquidURLs.http,
      origin = event.origin,
      message = event.data as string;

    if (origin && origin === baseDomain && message && message.includes("IFRAMELOADED")) {
      try {
        JSON.parse(message);
        this.start();
      } catch (e) {

      }
    }
  }
}

let mc = new MarketingClient();
mc.init();

WYBORCZA_PL.MarketingClient = mc;
